/**
 * NetSim — a latency simulator for localhost play (spec D10).
 *
 *   http://localhost:3000/?netsim=rtt:150,jitter:20,spike:2
 *
 * Adds rtt/2 each way, uniform jitter, and a `spike`% chance per message of an
 * RTT-sized stall (a TCP retransmit: everything behind it waits). Order is
 * always preserved, as on a real WebSocket. One tab can be laggy while another
 * is clean, so hit registration can be felt on one machine. Localhost only.
 */
export function parseNetSim(search, hostname) {
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') return null;
  const raw = new URLSearchParams(search || '').get('netsim');
  if (!raw) return null;
  const out = { rttMs: 0, jitterMs: 0, spikePct: 0 };
  for (const part of raw.split(',')) {
    const [key, value] = part.split(':');
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) continue;
    if (key === 'rtt') out.rttMs = n;
    else if (key === 'jitter') out.jitterMs = n;
    else if (key === 'spike') out.spikePct = n;
  }
  return out.rttMs > 0 || out.jitterMs > 0 ? out : null;
}

/** One direction of the simulated link. */
export class DelayLine {
  constructor({ baseMs = 0, jitterMs = 0, spikePct = 0, spikeMs = 0, random = Math.random, now = () => performance.now(), schedule = (fn, ms) => setTimeout(fn, ms) } = {}) {
    Object.assign(this, { baseMs, jitterMs, spikePct, spikeMs, random, now, schedule });
    this.last = -Infinity;
  }

  push(fn) {
    const t = this.now();
    let due = t + this.baseMs + this.random() * this.jitterMs;
    if (this.spikePct > 0 && this.random() * 100 < this.spikePct) due += this.spikeMs;
    this.last = Math.max(this.last, due);
    this.schedule(fn, this.last - t);
  }
}

/**
 * Delay a Socket.IO client socket both ways. Outgoing: wraps `emit`. Incoming:
 * wraps `onevent`, socket.io-client's (internal, stable since v3) per-event
 * dispatcher, so every handler sees the delay without being re-registered.
 */
export function installNetSim(socket, sim) {
  const common = { jitterMs: sim.jitterMs, spikePct: sim.spikePct, spikeMs: sim.rttMs };
  const up = new DelayLine({ ...common, baseMs: sim.rttMs / 2 });
  const down = new DelayLine({ ...common, baseMs: sim.rttMs / 2 });
  let generation = 0;
  const delay = (line, fn) => {
    const sentIn = generation;
    line.push(() => { if (sentIn === generation) fn(); });
  };
  socket.on('disconnect', () => {
    generation++;
    up.last = down.last = -Infinity;
  });
  const emit = socket.emit.bind(socket);
  socket.emit = (...args) => { delay(up, () => emit(...args)); return socket; };
  const onevent = socket.onevent.bind(socket);
  socket.onevent = (packet) => delay(down, () => onevent(packet));
  socket.netSimDelayOutbound = (fn) => delay(up, fn);
  return { up, down };
}
